import { Icon, type IconName } from "@/shared/ui";

/**
 * Иллюстрация пустой ленты: робот в кольцах, от него вниз тянется таймлайн, а
 * по сторонам — призраки будущих событий.
 *
 * Рисуется ТОЛЬКО когда лента пуста — так она нарисована и в макете. Это не
 * заставка экрана, а объяснение: «здесь будут появляться события», и над
 * заполненной лентой она была бы просто помехой.
 *
 * Всё собрано из CSS и SVG, ни одного растрового куска: сцена красится
 * токенами и тянется вместе с экраном. Координаты переведены в проценты от
 * исходных 360×305 один раз, здесь.
 *
 * Значения сняты со строк 199–208 `design-reference/css/screens.css` — из
 * последнего слоя переопределений: верхние правила в этом файле перекрыты, и
 * по ним орб и робот вышли бы другого размера.
 */

/** Призраки будущих событий: сторона, отступ и иконка. */
const GHOSTS = [
  { side: "left", offset: 12.5, top: 44.9, icon: "trendUp" },
  { side: "right", offset: 17.2, top: 55.1, icon: "robot" },
  { side: "left", offset: 16.7, top: 68.9, icon: "coinStack" },
  { side: "right", offset: 20.0, top: 81.0, icon: "trendUp" },
] as const;

/** Узлы таймлайна: отступ сверху в процентах высоты сцены. */
const DOTS = [51.5, 62.0, 73.4, 84.6] as const;

export function ActivityHero() {
  return (
    <div aria-hidden="true" className="relative mx-auto aspect-[360/305] w-full max-w-[360px]">
      {/* Орб: пунктирные кольца фоном, цветная дуга — границей повёрнутого
          круга, внутри волосяной обод. */}
      <span
        className={[
          // Ширина в процентах плюс квадратная пропорция: `size-[39.7%]` дал бы
          // высоту от высоты сцены, а не от ширины, и круг стал бы овалом.
          "absolute top-[1.6%] left-[30.3%] aspect-square w-[39.7%] rounded-full",
          "bg-[repeating-radial-gradient(circle,transparent_0_16px,color-mix(in_srgb,var(--color-status-info)_10%,transparent)_16px_17px,transparent_17px_24px)]",
        ].join(" ")}
      >
        <span
          className={[
            "absolute inset-2 rotate-[-32deg] rounded-full border-[3px] border-transparent",
            "border-l-status-info border-t-status-info border-r-ai-violet",
            "shadow-[0_0_11px_color-mix(in_srgb,var(--color-status-info)_25%,transparent),inset_0_0_10px_color-mix(in_srgb,var(--color-ai-violet)_12%,transparent)]",
          ].join(" ")}
        />
        <span className="absolute inset-[26px] rounded-full border border-status-info/20" />
      </span>

      {/* Орбиты — эллипсы под наклоном. Разный поворот и размер создают
          ощущение объёма без единой трёхмерной трансформации. */}
      <span className="absolute top-[18.7%] left-[21.9%] h-[23%] w-[56.1%] rotate-[7deg] rounded-[50%] border border-status-profit/55" />
      <span className="absolute top-[15.7%] left-[26.7%] h-[29.2%] w-[49.2%] rotate-[-13deg] rounded-[50%] border border-ai-violet/55" />

      <Satellite className="top-[14.8%] left-[21.1%]" />
      <Satellite className="top-[31.1%] right-[22.2%]" />

      <Robot />

      {/* Таймлайн от робота вниз. */}
      <span className="absolute top-[46.9%] left-[48.9%] h-[44.3%] w-px bg-[linear-gradient(180deg,color-mix(in_srgb,var(--color-status-profit)_36%,transparent),color-mix(in_srgb,var(--color-status-info)_11%,transparent),transparent)] shadow-[0_0_8px_color-mix(in_srgb,var(--color-status-profit)_12%,transparent)]">
        {DOTS.map((top) => (
          <span
            key={top}
            className="absolute -left-[3px] size-[7px] rounded-full border border-status-profit/35 bg-background-raised shadow-[0_0_var(--glow-blur-sm)_color-mix(in_srgb,var(--color-status-profit)_18%,transparent)]"
            style={{ top: `${String(top - 46.9)}%` }}
          />
        ))}
      </span>

      {GHOSTS.map((ghost) => (
        <Ghost key={`${ghost.side}-${String(ghost.top)}`} {...ghost} />
      ))}
    </div>
  );
}

function Satellite({ className }: { className: string }) {
  return (
    <span
      className={[
        "absolute size-3 rounded-full shadow-[0_0_var(--glow-blur-sm)_rgb(255_255_255/0.18)]",
        "bg-[radial-gradient(circle_at_35%_30%,var(--palette-metal-light),var(--palette-metal)_35%,var(--palette-metal-shadow)_72%)]",
        className,
      ].join(" ")}
    />
  );
}

/**
 * Робот. Сфера собрана радиальным градиентом со смещённым бликом — так шар
 * читается объёмным без единой картинки; глаза светятся собственной тенью.
 */
function Robot() {
  return (
    <span
      className={[
        "absolute top-[15.4%] left-[40%] aspect-square w-[20%] rounded-full",
        "bg-[radial-gradient(circle_at_34%_22%,var(--palette-metal)_0%,var(--palette-metal-dark)_28%,var(--palette-metal-shadow)_68%,var(--palette-bg)_100%)]",
        "shadow-[0_7px_18px_rgb(0_0_0/0.55),inset_0_1px_rgb(255_255_255/0.13)]",
      ].join(" ")}
    >
      <span className="absolute top-[37%] -left-[6%] h-[28%] w-[11%] rounded-full border border-white/10 bg-[linear-gradient(180deg,var(--palette-metal),var(--palette-metal-shadow))]" />
      <span className="absolute top-[37%] -right-[6%] h-[28%] w-[11%] rounded-full border border-white/10 bg-[linear-gradient(180deg,var(--palette-metal),var(--palette-metal-shadow))]" />

      <span className="absolute top-[29%] left-[19%] h-[40%] w-[62%] rounded-[11px] border border-white/15 bg-[linear-gradient(180deg,var(--palette-metal-panel),var(--palette-metal-panel-dark))] shadow-[inset_0_1px_rgb(255_255_255/0.12)]">
        <span className="absolute top-[38%] left-[31%] h-[28%] w-[7%] rounded-full bg-status-profit shadow-[0_0_var(--glow-blur-sm)_var(--color-status-profit)]" />
        <span className="absolute top-[38%] right-[31%] h-[28%] w-[7%] rounded-full bg-status-profit shadow-[0_0_var(--glow-blur-sm)_var(--color-status-profit)]" />
      </span>

      <span className="absolute inset-x-[19%] bottom-[6%] h-[10%] rounded-[50%] border border-status-profit/25 shadow-[0_0_7px_color-mix(in_srgb,var(--color-status-profit)_16%,transparent)]" />
    </span>
  );
}

function Ghost({
  side,
  offset,
  top,
  icon,
}: {
  side: "left" | "right";
  offset: number;
  top: number;
  icon: IconName;
}) {
  return (
    <span
      className={[
        "absolute grid h-[14%] w-[26%] grid-cols-[22px_1fr] items-center gap-1.5 rounded-sm p-1.5 opacity-35",
        "border border-white/4 bg-[linear-gradient(180deg,rgb(13_23_33/0.28),rgb(6_13_19/0.22))]",
      ].join(" ")}
      style={{ [side]: `${String(offset)}%`, top: `${String(top)}%` }}
    >
      <span className="flex size-[19px] items-center justify-center rounded-xs border border-status-profit/10 text-status-profit/60">
        <Icon name={icon} size={12} />
      </span>
      <span className="flex flex-col gap-1">
        <span className="h-[3px] w-[80%] rounded-full bg-white/12" />
        <span className="h-[3px] w-[62%] rounded-full bg-white/12" />
        <span className="h-[3px] w-[72%] rounded-full bg-white/12" />
      </span>
    </span>
  );
}
